import { navigateTo } from '../router';
import type { PackageAliasSummary, PackageSummary } from '../types';
import {
  buildTreeRoute,
  displayPackageName,
  isDocsOnlyQuality,
  primaryAlias,
  registryLabel,
  relativeTime,
  singleLanguageDotClass,
} from '../utils';

export interface PackageGroup {
  registry: string;
  name: string;
  versions: PackageSummary[];
  aliases: PackageAliasSummary[];
  latestVersion: string;
  displayVersion: string;
  indexedAt: string;
  releasedAt: string | null;
  quality: number | null;
  qualityNote: string | null;
  summary: string | null;
  languages: string[];
}

function registryBlurb(registry: string): string {
  if (registry === 'pypi') return 'Published Python package with indexed docs and source.';
  if (registry === 'npm') return 'JavaScript or TypeScript package ready for code retrieval.';
  if (registry === 'pub') return 'Dart or Flutter package indexed for precise context.';
  if (registry === 'github') return 'Repository index with implementation details and examples.';
  return 'Indexed package available for retrieval and semantic search.';
}

function summarizePackage(group: PackageGroup): string {
  return group.summary?.trim() || registryBlurb(group.registry);
}

// The score is only worth a row of pixels when it's actionable. A docs-only or
// sub-50 index changes what an agent will get back; 80-plus is the norm and
// painting it on all 975 rows just adds colour. The full ordering stays
// available through the "Quality score" sort.
function qualityFlag(group: PackageGroup): { label: string; tone: string; title: string } | null {
  if (group.quality == null) return null;
  const title = group.qualityNote
    ? `Index quality ${group.quality}% — ${group.qualityNote}`
    : `Index quality ${group.quality}%`;
  if (isDocsOnlyQuality(group.qualityNote)) return { label: 'docs-only', tone: 'text-del', title };
  if (group.quality < 50) return { label: `${group.quality}% indexed`, tone: 'text-warn', title };
  return null;
}

/**
 * One catalog entry as a directory row. From `md` up the right-hand cluster
 * uses fixed widths rather than intrinsic sizing, so version / count / date
 * line up as real columns down the list — the alignment a card grid can't give.
 * Below `md` there's no room for those columns, so the row grows a third line
 * carrying the same provenance rather than dropping it.
 */
export function PackageRow({ group }: { group: PackageGroup }) {
  const displayName = displayPackageName(group.registry, primaryAlias(group)?.name ?? group.name);
  const href = buildTreeRoute({
    registry: group.registry,
    name: group.name,
    version: group.latestVersion,
  });
  const flag = qualityFlag(group);
  const versionCount = group.versions.length;

  return (
    <div
      data-testid={`browse-package-${group.registry}-${group.name}`}
      onClick={() => navigateTo(href)}
      className="group grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-4 py-2.5 transition-colors hover:bg-accent-soft"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full ${singleLanguageDotClass(group.languages[0] ?? '')}`}
          />
          {/* A real anchor so middle-click and copy-link work; the row-level
              onClick handles the ordinary case. */}
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateTo(href);
            }}
            className="truncate text-sm font-semibold text-ink outline-none transition-colors group-hover:text-accent focus-visible:underline"
            title={displayName}
          >
            {displayName}
          </a>
          {/* Registry sits inline only once the right-hand columns exist; below
              `md` it moves into the meta line so nothing is duplicated. */}
          <span className="hidden shrink-0 text-2xs uppercase tracking-wide text-dim md:inline">
            {registryLabel(group.registry)}
          </span>
          {flag && (
            <span className={`shrink-0 text-2xs font-medium ${flag.tone}`}>{flag.label}</span>
          )}
        </div>
        {/* Two wrapped lines on narrow screens, one truncated line once the row
            is wide. `line-clamp-none` is required to undo the `-webkit-box`
            display that `line-clamp-2` sets — `truncate` alone won't reset it. */}
        <p className="mt-0.5 line-clamp-2 pl-3.5 text-xs text-dim md:line-clamp-none md:truncate">
          {summarizePackage(group)}
        </p>
        {/* Narrow screens have no room for the right-hand columns, so the same
            provenance rides along here instead of being dropped. */}
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 pl-3.5 text-2xs text-dim md:hidden">
          <span className="uppercase tracking-wide">{registryLabel(group.registry)}</span>
          <span aria-hidden="true">·</span>
          <span>
            {versionCount} version{versionCount === 1 ? '' : 's'}
          </span>
          <span aria-hidden="true">·</span>
          <span title={group.indexedAt}>indexed {relativeTime(group.indexedAt)}</span>
        </p>
      </div>

      <div className="flex shrink-0 items-start gap-4 self-start pt-0.5 text-2xs text-dim md:items-center md:self-auto md:pt-0">
        <span
          className="w-20 truncate text-right font-mono text-link md:w-24"
          title={group.displayVersion}
        >
          {group.displayVersion}
        </span>
        <span className="hidden w-20 text-right tabular-nums md:block">
          {versionCount} version{versionCount === 1 ? '' : 's'}
        </span>
        <span className="hidden w-12 text-right md:block" title={group.indexedAt}>
          {relativeTime(group.indexedAt)}
        </span>
      </div>
    </div>
  );
}
